"use client";
import Image from "next/image";
import { ImageType, isAvailable, LinkType } from "@/types/baseTypes.ts";
import styles from "./MediaItem.module.scss";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { generateMediaFormat } from "@/utils/layout/mediaFormat.ts";
import { ComponentProps, useMemo } from "react";
import { useBreakpoint, Breakpoint } from "@/hooks/useBreakpoint";
import MediaInfo from "@/components/MediaInfo/MediaInfo";
import { customSanitize } from "@/utils/customSanitize";
import { CustomLink } from "../CustomLink";

interface BaseMediaItemProps {
  quality?: number;
  mediaFormat?: MediaFormatType;
  priority?: boolean;
  className?: string;
  objectFit?: "cover" | "contain";
  mediaInfoPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  figcaption?: string;
  caption_position?: "has-text-left" | "has-text-centered" | "has-text-right";
}

interface MediaItemResponsiveProps extends Omit<BaseMediaItemProps, "sizes"> {
  image_mobile?: ImageType | false;
  image_tablet?: ImageType | false;
  image_desktop: ImageType | false;
  image_fullhd?: ImageType | false;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  objectFit?: "contain" | "cover";
  mediaFormat?: MediaFormatType;
  className?: string;
  mediaInfoPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  link?: LinkType;
  linkProps?: ComponentProps<"a">;
  disablePrefetch?: boolean;
  hasOverlay?: boolean;
}

const selectImageForBreakpoint = (
  breakpoint: Breakpoint,
  images: {
    mobile?: ImageType | false;
    tablet?: ImageType | false;
    desktop: ImageType | false;
    fullhd?: ImageType | false;
  },
): ImageType | false => {
  const { mobile, tablet, desktop, fullhd } = images;

  switch (breakpoint) {
    case "mobile":
      return (
        (mobile && isAvailable<ImageType>(mobile) ? mobile : desktop) || false
      );
    case "tablet":
      return (
        (tablet && isAvailable<ImageType>(tablet) ? tablet : desktop) || false
      );
    case "desktop":
    case "widescreen":
      return desktop || false;
    case "fullhd":
      return (
        (fullhd && isAvailable<ImageType>(fullhd) ? fullhd : desktop) || false
      );
    default:
      return desktop || false;
  }
};

export const MediaItemResponsive = ({
  image_mobile,
  image_tablet,
  image_desktop,
  image_fullhd,
  sizes = "100vw",
  quality = 90,
  priority = false,
  objectFit = "cover",
  mediaFormat,
  className = "",
  mediaInfoPosition = "bottom-right",
  figcaption,
  caption_position,
  link,
  linkProps,
  disablePrefetch,
  hasOverlay,
}: MediaItemResponsiveProps) => {
  const breakpoint = useBreakpoint();

  const selectedImage = useMemo(
    () =>
      selectImageForBreakpoint(breakpoint, {
        mobile: image_mobile,
        tablet: image_tablet,
        desktop: image_desktop,
        fullhd: image_fullhd,
      }),
    [breakpoint, image_mobile, image_tablet, image_desktop, image_fullhd],
  );

  if (!selectedImage || !isAvailable<ImageType>(selectedImage)) {
    return null;
  }

  const isSVG = selectedImage.mime_type?.includes("svg") ?? false;

  const blurDataURL =
    selectedImage.image_blur && selectedImage.mime_type && !isSVG
      ? `data:${selectedImage.mime_type};base64,${selectedImage.image_blur}`
      : undefined;

  const classNameString = `image-wrapper ${styles["image-wrapper"]} ${className} ${
    mediaFormat && !isSVG ? generateMediaFormat(mediaFormat) : ""
  }`;

  const infoButtonStyle: React.CSSProperties = {
    position: "absolute",
    zIndex: 12,
    ...(mediaInfoPosition.includes("top")
      ? { top: "1rem" }
      : { bottom: "1rem" }),
    ...(mediaInfoPosition.includes("left")
      ? { left: "1rem" }
      : { right: "1rem" }),
  };

  return (
    <figure className={classNameString} style={{ position: "relative" }}>
      {hasOverlay && <div className={styles["overlay"]} />}

      <div className="is-relative h-100 w-100">
        <Image
          src={selectedImage.url}
          alt={selectedImage.alt || selectedImage.title || ""}
          width={selectedImage.width}
          height={selectedImage.height}
          sizes={sizes}
          quality={quality}
          priority={priority}
          blurDataURL={blurDataURL}
          placeholder={blurDataURL ? "blur" : undefined}
          style={{
            width: "100%",
            height: "100%",
            objectFit: objectFit,
          }}
          className="responsive-image"
        />

        <div style={infoButtonStyle}>
          <MediaInfo
            disableAbsolute
            image_desktop={image_desktop}
            image_fullhd={image_fullhd}
            image_tablet={image_tablet}
            image_mobile={image_mobile}
            media={
              <figure className={classNameString}>
                <Image
                  src={selectedImage.url}
                  alt={selectedImage.alt || selectedImage.title || ""}
                  width={selectedImage.width}
                  height={selectedImage.height}
                  sizes="100vw"
                  quality={quality}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: objectFit,
                  }}
                />
              </figure>
            }
          />
        </div>
      </div>

      {/* jscpd:ignore-start */}
      {link && link.url && (
        <CustomLink
          href={link.url}
          target={link.target ?? "_self"}
          {...(link["aria_label"] && { "aria-label": link["aria_label"] })}
          disablePrefetch={disablePrefetch}
          className="full-absolute-hidden-link"
          {...linkProps}
        >
          Link
        </CustomLink>
      )}
      {/* jscpd:ignore-end */}

      {figcaption && (
        <figcaption
          className={`caption ${caption_position}`}
          dangerouslySetInnerHTML={{ __html: customSanitize(figcaption) }}
        />
      )}
    </figure>
  );
};
